import React, { FormEvent, useId, useState } from "react";
import {
  Badge,
  Checkbox,
  LoadingButton,
  RadioGroup,
  RadioGroupItem,
  Stack,
  Textarea,
  Typography,
} from "@altimateai/lego";
import { AskUserQuestionPayload } from "../../types";
import { useAgentChat } from "../../hooks/useAgentChat";
import {
  AskUserQuestionAnswer,
  formatAskUserQuestionAnswers,
  isAnswered,
} from "../../utils/askUserQuestion";

interface AskUserQuestionCardProps {
  question: AskUserQuestionPayload;
  /** Only the latest turn can be answered; older cards render read-only. */
  interactive: boolean;
}

type AskUserQuestionOption = AskUserQuestionPayload["questions"][number]["options"][number];

const emptyAnswer = (): AskUserQuestionAnswer => ({ selected: [], other: "" });

const optionText = (option: AskUserQuestionOption) => (
  <span>
    <Typography variant="body" className="al-block">
      {option.label}
    </Typography>
    {option.description && (
      <Typography variant="caption" className="al-block al-text-muted-foreground">
        {option.description}
      </Typography>
    )}
  </span>
);

const optionRowClass = "al-flex al-items-start al-gap-2 al-cursor-pointer";

/**
 * The agent's clarifying question (Claude's `AskUserQuestion` tool),
 * rendered as a card. Submitting sends the answers as the user's next message,
 * which continues the session. Typing a normal message works as a reply too.
 */
export const AskUserQuestionCard: React.FC<AskUserQuestionCardProps> = ({
  question,
  interactive,
}) => {
  const { sendMessage } = useAgentChat();
  const idPrefix = useId();
  const [answers, setAnswers] = useState<AskUserQuestionAnswer[]>(() =>
    question.questions.map(emptyAnswer)
  );
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const editable = interactive && !submitted;
  const canSubmit =
    editable && !submitting && question.questions.every((_, i) => isAnswered(answers[i]));

  const updateAnswer = (index: number, update: Partial<AskUserQuestionAnswer>) =>
    setAnswers(prev => prev.map((answer, i) => (i === index ? { ...answer, ...update } : answer)));

  const toggleOption = (index: number, label: string, checked: boolean) => {
    const current = answers[index]?.selected ?? [];
    updateAnswer(index, {
      selected: checked ? [...current, label] : current.filter(value => value !== label),
    });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await sendMessage({ message: formatAskUserQuestionAnswers(question, answers) });
      setSubmitted(true);
    } catch (error) {
      console.error("Failed to send the answer:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      role="region"
      aria-label="Question from the agent"
      className="al-rounded-lg al-border al-border-border al-p-4 al-space-y-4"
    >
      {question.questions.map((item, index) => (
        <fieldset key={`${index}-${item.question}`} className="al-space-y-2" disabled={!editable}>
          <legend className="al-space-y-1">
            {item.header && <Badge variant="secondary">{item.header}</Badge>}
            <Typography variant="body" className="al-block al-font-medium">
              {item.question}
            </Typography>
          </legend>
          <Stack direction="column" gap="sm">
            {item.multiSelect ? (
              item.options.map((option, optionIndex) => {
                const id = `${idPrefix}-${index}-${optionIndex}`;
                return (
                  <label key={id} htmlFor={id} className={optionRowClass}>
                    <Checkbox
                      id={id}
                      className="al-mt-0.5"
                      checked={answers[index]?.selected.includes(option.label) ?? false}
                      onCheckedChange={checked =>
                        toggleOption(index, option.label, checked === true)
                      }
                      disabled={!editable}
                      aria-label={option.label}
                    />
                    {optionText(option)}
                  </label>
                );
              })
            ) : (
              <RadioGroup
                value={answers[index]?.selected[0] ?? ""}
                onValueChange={value => updateAnswer(index, { selected: [value], other: "" })}
                disabled={!editable}
                aria-label={item.question}
              >
                {item.options.map((option, optionIndex) => {
                  const id = `${idPrefix}-${index}-${optionIndex}`;
                  return (
                    <label key={id} htmlFor={id} className={optionRowClass}>
                      <RadioGroupItem
                        id={id}
                        className="al-mt-0.5"
                        value={option.label}
                        aria-label={option.label}
                      />
                      {optionText(option)}
                    </label>
                  );
                })}
              </RadioGroup>
            )}
            <Textarea
              value={answers[index]?.other ?? ""}
              onChange={event =>
                // A single-select question takes one answer: typed text
                // replaces the chosen option (and choosing one clears the text).
                updateAnswer(
                  index,
                  item.multiSelect || !event.target.value.trim()
                    ? { other: event.target.value }
                    : { other: event.target.value, selected: [] }
                )
              }
              placeholder="Or type your own answer"
              aria-label={`Your own answer: ${item.question}`}
              disabled={!editable}
              rows={1}
            />
          </Stack>
        </fieldset>
      ))}
      {interactive && (
        <Stack justify="end">
          <LoadingButton type="submit" loading={submitting} disabled={!canSubmit}>
            {submitted ? "Answer sent" : "Send answer"}
          </LoadingButton>
        </Stack>
      )}
    </form>
  );
};
